/**
 * Audio Engine
 * Handles audio playback using Web Audio API
 */

export class AudioEngine {
  private audioContext: AudioContext | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private scriptProcessorNode: ScriptProcessorNode | null = null;
  private isInitialized = false;
  private isMuted = false;
  private volume = 1.0;
  private gainNode: GainNode | null = null;

  // Audio buffer settings
  private readonly SAMPLE_RATE = 32768; // DS audio sample rate
  private readonly BUFFER_SIZE = 2048;
  private readonly CHANNELS = 2; // Stereo

  /**
   * Initialize the audio engine
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Create audio context
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: this.SAMPLE_RATE,
        latencyHint: 'interactive',
      });

      // Create gain node for volume control
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.volume;
      this.gainNode.connect(this.audioContext.destination);

      // Try to use AudioWorklet (modern approach)
      // If that fails, fall back to ScriptProcessor (deprecated but more compatible)
      try {
        await this.setupAudioWorklet();
      } catch (error) {
        console.warn('AudioWorklet not available, falling back to ScriptProcessor');
        this.setupScriptProcessor();
      }

      this.isInitialized = true;
      console.log('✓ Audio engine initialized');
    } catch (error) {
      console.error('Failed to initialize audio engine:', error);
      throw error;
    }
  }

  /**
   * Setup modern AudioWorklet
   */
  private async setupAudioWorklet(): Promise<void> {
    if (!this.audioContext) {
      throw new Error('Audio context not initialized');
    }

    // Note: This requires an AudioWorklet processor file
    // For now, we'll use ScriptProcessor as it's simpler for the MVP
    throw new Error('AudioWorklet setup not implemented yet');
  }

  /**
   * Setup ScriptProcessor (fallback)
   */
  private setupScriptProcessor(): void {
    if (!this.audioContext || !this.gainNode) {
      throw new Error('Audio context not initialized');
    }

    // Create script processor
    this.scriptProcessorNode = this.audioContext.createScriptProcessor(
      this.BUFFER_SIZE,
      0, // no input channels
      this.CHANNELS // stereo output
    );

    // Connect to gain node
    this.scriptProcessorNode.connect(this.gainNode);
  }

  /**
   * Process audio samples from emulator
   */
  processAudio(audioBuffer: Float32Array | null, callback?: (event: AudioProcessingEvent) => void): void {
    if (!this.scriptProcessorNode || !audioBuffer) {
      return;
    }

    // Set audio processing callback
    this.scriptProcessorNode.onaudioprocess = (event) => {
      const outputL = event.outputBuffer.getChannelData(0);
      const outputR = event.outputBuffer.getChannelData(1);

      // Fill output buffers with emulator audio data
      const samplesNeeded = event.outputBuffer.length;

      for (let i = 0; i < samplesNeeded && i < audioBuffer.length / 2; i++) {
        outputL[i] = audioBuffer[i * 2]; // Left channel
        outputR[i] = audioBuffer[i * 2 + 1]; // Right channel
      }

      // Call custom callback if provided
      if (callback) {
        callback(event);
      }
    };
  }

  /**
   * Resume audio context (required after user interaction)
   */
  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      console.log('✓ Audio context resumed');
    }
  }

  /**
   * Suspend audio context
   */
  async suspend(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'running') {
      await this.audioContext.suspend();
    }
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));

    if (this.gainNode) {
      this.gainNode.gain.value = this.isMuted ? 0 : this.volume;
    }
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Mute audio
   */
  mute(): void {
    this.isMuted = true;
    if (this.gainNode) {
      this.gainNode.gain.value = 0;
    }
  }

  /**
   * Unmute audio
   */
  unmute(): void {
    this.isMuted = false;
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
  }

  /**
   * Toggle mute
   */
  toggleMute(): void {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  /**
   * Check if muted
   */
  isMutedState(): boolean {
    return this.isMuted;
  }

  /**
   * Get audio context state
   */
  getState(): string {
    return this.audioContext?.state || 'closed';
  }

  /**
   * Check if initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.audioContext !== null;
  }

  /**
   * Cleanup and destroy audio engine
   */
  destroy(): void {
    if (this.scriptProcessorNode) {
      this.scriptProcessorNode.disconnect();
      this.scriptProcessorNode = null;
    }

    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }

    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.isInitialized = false;
  }
}
